import { useEffect, useState } from 'react'
import { useLocation } from 'react-router'
import { CartBar, EmptyState, ExtraCard, Skeleton } from '@/ds'
import { LinkRow } from '@/app/components/LinkRow'
import { useLoadedStay } from '@/app/stay/StayProvider'
import { formatMoney, formatMoneyShort } from '@/lib/format'
import { useAppNavigate } from '@/lib/query-params'
import { asyncValue, useAsync } from '@/lib/useAsync'
import { extrasService } from '@/services/extras'
import type { Extra } from '@/services/extras'
import { reservaService } from '@/services/reserva'
import type { ChangeType } from '@/services/reserva'
import { useApiLang } from '../Checkin/useSave'
import { SectionError } from '../Estancia/sections/SectionError'
import { BackLink } from '../Pagos/shared'
import { conditionFor } from '../Reserva/Reserva'
import { BOOKING_ROUTES, changeQuery, useLang } from '../Reserva/shared'
import { EXTRAS_ROUTES, lineAmount, priceText, quantityFor, readCart, writeCart } from './shared'
import { PartnerExperiences } from './PartnerExperiences'
import { featureEnabled } from '@/config/features'

const TIME_CHANGES: ChangeType[] = ['early_checkin', 'late_checkout']

/**
 * 8.6 · Extras para tu estancia (PantallaExtras.dc.html, Bloque 6 §6a). Catálogo contratable hasta la salida con
 * carrito y barra fija. Catálogo real (E9); descripción, foto y cobro en la app solo con mock (D46). Entrar antes y
 * salir más tarde no se compran aquí: enlazan al flujo de cambios de la reserva (D47).
 */
export function ExtrasCatalogo() {
  const { t, lang } = useLang()
  const apiLang = useApiLang()
  const stay = useLoadedStay()
  const { go } = useAppNavigate()
  const highlight = (useLocation().state as { extraId?: string | null } | null)?.extraId ?? null
  const [data, reload] = useAsync(`extras:${stay.guestCode}`, (signal) => extrasService.list(stay, signal))
  const [options] = useAsync(`extras-cambios:${stay.guestCode}:${stay.dates.checkIn}:${stay.dates.checkOut}`, (signal) => reservaService.getChangeOptions(stay, apiLang, signal))
  const [cart, setCart] = useState(() => readCart(stay.guestCode))
  const [now] = useState(() => new Date())
  const showPhotos = featureEnabled('extrasFotos')

  const extras = asyncValue(data) ?? []
  const addable = extras.filter((e) => e.kind === 'extra')
  const buyable = addable.filter((e) => !e.alreadyAdded)
  const inCart = (id: string) => cart.some((c) => c.extraId === id)

  // Desde una recomendación: se señala el extra. Si es entrar antes o salir más tarde, su camino es el cambio de reserva (D47).
  const highlighted = extras.find((e) => e.id === highlight) ?? null
  useEffect(() => {
    if (!highlighted) return
    if (highlighted.kind !== 'extra') go(BOOKING_ROUTES.pick(highlighted.kind), { replace: true, query: changeQuery('extras') })
    else document.getElementById(`extra-${highlighted.id}`)?.scrollIntoView?.({ block: 'center' })
    // go cambia en cada render; solo importa cuando llega el catálogo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlighted])

  const toggle = (extra: Extra) => {
    const next = inCart(extra.id) ? cart.filter((c) => c.extraId !== extra.id) : [...cart, { extraId: extra.id, quantity: quantityFor(extra, stay) }]
    setCart(next)
    writeCart(stay.guestCode, next)
  }

  // Filas al flujo de cambios (D47): las que ofrece la reserva y las que E9 trae como extra con ese nombre.
  const o = asyncValue(options)
  const timeRows = TIME_CHANGES.flatMap((type) => {
    const option = o?.options.find((x) => x.type === type && x.allowed)
    const asExtra = extras.find((e) => e.kind === type)
    if (!option && !asExtra) return []
    const subtitle = option && o ? conditionFor(option, o, stay, lang, t, now) : asExtra ? t('v2.extras.cambios.precio', { price: asExtra.price.amount === 0 ? t('v2.extras.gratis') : formatMoneyShort(asExtra.price, lang) }) : undefined
    return [{ type, subtitle }]
  })

  const selected = cart.flatMap((c) => {
    const extra = buyable.find((e) => e.id === c.extraId)
    return extra ? [{ extra, quantity: c.quantity }] : []
  })
  const total = selected.reduce((sum, s) => sum + lineAmount(s.extra, s.quantity).amount, 0)

  return (
    <main className="page">
      <div className="pagos__head">
        <BackLink />
        <h1>{t('v2.extras.titulo')}</h1>
        <p className="page__lead">{extrasService.paysInApp ? t('v2.extras.lead') : t('v2.extras.leadReal')}</p>
      </div>

      {data.kind === 'loading' && (
        <div className="extras__list" aria-busy="true">
          <Skeleton height={320} />
          <Skeleton height={320} />
        </div>
      )}
      {data.kind === 'error' && <SectionError code={data.code} onRetry={reload} />}
      {(data.kind === 'unsupported' || (data.kind === 'ready' && addable.length === 0 && timeRows.length === 0)) && (
        <EmptyState icon="shopping-bag" title={t('v2.extras.vacioTitulo')} headingLevel={2}>
          {t('v2.extras.vacioTexto')}
        </EmptyState>
      )}

      {data.kind === 'ready' && addable.length > 0 && (
        <div className="extras__list">
          {addable.map((extra) => (
            <div key={extra.id} id={`extra-${extra.id}`} className={extra.id === highlight ? 'extras__highlight' : undefined}>
              <ExtraCard
                name={extra.name}
                headingLevel={2}
                price={priceText(extra, lang, t)}
                {...(extra.description ? { description: extra.description } : {})}
                {...(showPhotos && extra.photoUrl ? { image: extra.photoUrl } : {})}
                imageLabel={t('v2.extras.foto', { name: extra.name })}
                added={inCart(extra.id) && !extra.alreadyAdded}
                {...(extra.alreadyAdded ? { disabled: true, addLabel: t('v2.legacy.preview.labels.availableExtrasView.alreadyContracted') } : { onAdd: () => toggle(extra) })}
              />
            </div>
          ))}
        </div>
      )}

      {data.kind === 'ready' && timeRows.length > 0 && (
        <section className="pagos__group" aria-labelledby="extras-cambios">
          <h2 id="extras-cambios" className="pagos__h2">
            {t('v2.extras.cambios.titulo')}
          </h2>
          <p className="pagos__note">{t('v2.extras.cambios.texto')}</p>
          {timeRows.map((row) => (
            <LinkRow key={row.type} icon="clock" title={t(`v2.reserva.cambios.${row.type}`)} {...(row.subtitle ? { subtitle: row.subtitle } : {})} to={BOOKING_ROUTES.pick(row.type)} query={changeQuery('extras')} tabular />
          ))}
        </section>
      )}

      <PartnerExperiences />

      <CartBar
        count={selected.length}
        total={formatMoney({ amount: total, currency: stay.currency }, lang)}
        onPay={() => go(EXTRAS_ROUTES.confirm)}
        {...(extrasService.paysInApp ? {} : { actionLabel: t('v2.extras.continuar') })}
        className="extras__cart"
      />
    </main>
  )
}
